import React, { useCallback, useRef, useState } from 'react';

/**
 * Resizable two-pane split panel.
 *
 * Props:
 *  left        – left pane content
 *  right       – right pane content
 *  defaultLeft – initial left pane width in px (default 320)
 *  minLeft     – minimum left pane width in px (default 160)
 *  minRight    – minimum right pane width in px (default 200)
 *  height      – CSS height of the whole container (default 'calc(100vh - 260px)')
 *  storageKey  – localStorage key to persist the left pane width across page loads (optional)
 */
export function SplitPanel({
    left,
    right,
    defaultLeft = 320,
    minLeft = 160,
    minRight = 200,
    height = 'calc(100vh - 260px)',
    storageKey,
}) {
    const [leftWidth, setLeftWidth] = useState(() => {
        if (storageKey) {
            const stored = localStorage.getItem(storageKey);
            if (stored !== null) {
                const parsed = parseInt(stored, 10);
                if (!isNaN(parsed)) return parsed;
            }
        }
        return defaultLeft;
    });
    const containerRef = useRef(null);
    const dragging = useRef(false);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        dragging.current = true;

        const onMouseMove = (ev) => {
            if (!dragging.current || !containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const newWidth = ev.clientX - rect.left;
            if (newWidth >= minLeft && newWidth <= rect.width - minRight - 8) {
                setLeftWidth(newWidth);
                if (storageKey) localStorage.setItem(storageKey, newWidth);
            }
        };

        const onMouseUp = () => {
            dragging.current = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }, [minLeft, minRight, storageKey]);

    return (
        <div
            ref={containerRef}
            style={{ display: 'flex', height, width: '100%', overflow: 'hidden' }}
        >
            {/* Left pane */}
            <div style={{ width: leftWidth, flexShrink: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {left}
            </div>

            {/* Drag handle */}
            <div
                onMouseDown={handleMouseDown}
                style={{
                    width: 8,
                    flexShrink: 0,
                    cursor: 'col-resize',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    transition: 'background 0.15s',
                    zIndex: 1,
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--base-border-color, rgba(0,0,0,0.1))'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
            >
                <div style={{
                    width: 2,
                    height: 48,
                    borderRadius: 2,
                    background: 'var(--base-border-color, #ccc)',
                    opacity: 0.6,
                    pointerEvents: 'none',
                }} />
            </div>

            {/* Right pane */}
            <div style={{ flex: 1, minWidth: minRight, overflow: 'auto' }}>
                {right}
            </div>
        </div>
    );
}
